<template>
  <div class="fellowship-reports card">
    <!-- Header -->
    <div class="d-flex justify-content-between align-items-center mb-4 card-header">
      <div>
        <h4 class="card-title mb-0">
          <i class="bi bi-file-earmark-bar-graph me-2"></i>Reports
        </h4>
      </div>
      <div class="d-flex gap-2">
        <button
          class="btn btn-outline-primary"
          @click="refreshAllData"
          :disabled="loading"
        >
          <i class="bi bi-arrow-clockwise me-2"></i>
          {{ loading ? 'Loading...' : 'Refresh' }}
        </button>
        <button
          class="btn btn-success"
          @click="exportToExcel"
          :disabled="exporting"
          style="background: #009CAD; border: #009CAD; color: #fff"
        >
          <i class="bi bi-download me-2"></i>
          {{ exporting ? 'Exporting...' : 'Export Excel' }}
        </button>
      </div>
    </div>

    <!-- Error Alert -->
    <div v-if="error" class="alert alert-danger alert-dismissible fade show" role="alert">
      <i class="bi bi-exclamation-triangle me-2"></i>
      {{ error }}
      <button type="button" class="btn-close" @click="error = null"></button>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="text-center py-5">
      <div class="spinner-border text-primary mb-3" style="width: 3rem; height: 3rem;"></div>
      <p class="text-muted">Loading fellowship statistics...</p>
      <small class="text-muted">This may take a few moments while we fetch data from the database.</small>
    </div>

    <!-- Main Content -->
    <div class="card-body" v-else>
      <!-- Fellows Distribution by Country -->
      <div class="row mb-4">
        <div class="col-12">
          <div class="card border-0 shadow-sm">
            <div class="card-header bg-white border-bottom">
              <h5 class="mb-0">
                <i class="bi bi-geo-alt me-2 text-primary"></i>
                Fellows Distribution by Country
              </h5>
            </div>
            <div class="card-body">
              <div class="row">
                <div class="col-lg-8">
                  <div id="africa-map" style="height: 500px; background: #f8f9fa; border-radius: 8px;"></div>
                </div>
                <div class="col-lg-4">
                  <div class="country-stats">
                    <div class="d-flex justify-content-between align-items-center mb-3">
                      <h6 class="mb-0">Fellows by Country</h6>
                      <span class="badge bg-primary">{{ Object.keys(fellowshipStats.fellowships_per_country?.all || {}).length }} Countries</span>
                    </div>
                    <div class="country-list" style="max-height: 400px; overflow-y: auto;">
                      <div
                        v-for="(count, country) in sortedCountries"
                        :key="country"
                        class="d-flex justify-content-between align-items-center py-2 border-bottom"
                      >
                        <span class="country-name">{{ country }}</span>
                        <span class="badge bg-light text-dark">{{ count }}</span>
                      </div>
                      <div v-if="Object.keys(sortedCountries).length === 0" class="text-center py-4 text-muted">
                        <i class="bi bi-info-circle"></i>
                        <p class="mt-2 mb-0">No country data available</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Fellowships Awarded per Year -->
      <div class="row mb-4">
        <div class="col-12">
          <div class="card border-0 shadow-sm">
            <div class="card-header bg-white border-bottom">
              <h5 class="mb-0">
                <i class="bi bi-bar-chart me-2 text-primary"></i>
                Fellowships Awarded per Year
              </h5>
            </div>
            <div class="card-body">
              <div v-if="!hasData('fellowships_per_year')" class="text-center py-4 text-muted">
                <i class="bi bi-bar-chart" style="font-size: 2rem;"></i>
                <p class="mt-2">No fellowship year data available</p>
              </div>
              <div v-else>
                <canvas
                  ref="fellowshipsPerYearChart"
                  style="max-height: 400px;"
                  width="800"
                  height="400"
                ></canvas>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- EDCTP Fellowship Calls -->
      <div class="row mb-4">
        <div class="col-12">
          <div class="card border-0 shadow-sm">
            <div class="card-header bg-white border-bottom">
              <h5 class="mb-0">
                <i class="bi bi-pie-chart me-2 text-success"></i>
                EDCTP Fellowship Calls
              </h5>
              <div class="text-muted small">Total: {{ getTotalFellowshipCalls() }}</div>
            </div>
            <div class="card-body">
              <div v-if="!hasData('fellowships_per_call')" class="text-center py-4 text-muted">
                <i class="bi bi-pie-chart" style="font-size: 2rem;"></i>
                <p class="mt-2">No fellowship call data available</p>
              </div>
              <div v-else>
                <canvas
                  ref="fellowshipCallsChart"
                  style="max-height: 400px;"
                  width="800"
                  height="400"
                ></canvas>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Gender Distribution -->
      <div class="row mb-4">
        <div class="col-12">
          <div class="card border-0 shadow-sm">
            <div class="card-header bg-white border-bottom">
              <h5 class="mb-0">
                <i class="bi bi-gender-ambiguous me-2 text-info"></i>
                Gender Distribution
              </h5>
            </div>
            <div class="card-body">
              <div v-if="!hasData('gender_distribution')" class="text-center py-4 text-muted">
                <i class="bi bi-gender-ambiguous" style="font-size: 2rem;"></i>
                <p class="mt-2">No gender data available</p>
              </div>
              <div v-else>
                <canvas
                  ref="genderChart"
                  style="max-height: 400px;"
                  width="800"
                  height="400"
                ></canvas>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Additional Projects Reported by Fellows -->
      <div class="row mb-4">
        <div class="col-12">
          <div class="card border-0 shadow-sm">
            <div class="card-header bg-white border-bottom">
              <h5 class="mb-0">
                <i class="bi bi-folder me-2 text-info"></i>
                Additional Projects Reported by Fellows
              </h5>
            </div>
            <div class="card-body">
              <div v-if="!hasProjectData()" class="text-center py-4 text-muted">
                <i class="bi bi-folder" style="font-size: 2rem;"></i>
                <p class="mt-2">No project data available</p>
              </div>
              <div v-else>
                <canvas
                  ref="projectsChart"
                  style="max-height: 400px;"
                  width="800"
                  height="400"
                ></canvas>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Publications by Fellows -->
      <div class="row mb-4">
        <div class="col-12">
          <div class="card border-0 shadow-sm">
            <div class="card-header bg-white border-bottom">
              <h5 class="mb-0">
                <i class="bi bi-journal-text me-2 text-warning"></i>
                Publications by Fellows
              </h5>
            </div>
            <div class="card-body">
              <div v-if="!hasPublicationData('publications_per_year')" class="text-center py-4 text-muted">
                <i class="bi bi-journal-text" style="font-size: 2rem;"></i>
                <p class="mt-2">No publication data available</p>
              </div>
              <div v-else>
                <canvas
                  ref="publicationsChart"
                  style="max-height: 400px;"
                  width="800"
                  height="400"
                ></canvas>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Average Publications During Fellowship -->
      <div class="row mb-4">
        <div class="col-12">
          <div class="card border-0 shadow-sm">
            <div class="card-header bg-white border-bottom">
              <h5 class="mb-0">
                <i class="bi bi-graph-up me-2 text-primary"></i>
                Average Publications During Fellowship
              </h5>
            </div>
            <div class="card-body">
              <div v-if="!hasPublicationData('publications_during_fellowship')" class="text-center py-4 text-muted">
                <i class="bi bi-graph-up" style="font-size: 2rem;"></i>
                <p class="mt-2">No publication data available</p>
              </div>
              <div v-else>
                <canvas
                  ref="avgPublicationsDuringChart"
                  style="max-height: 400px;"
                  width="800"
                  height="400"
                ></canvas>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Publications Before/After Fellowship -->
      <div class="row mb-4">
        <div class="col-12">
          <div class="card border-0 shadow-sm">
            <div class="card-header bg-white border-bottom">
              <h5 class="mb-0">
                <i class="bi bi-graph-up-arrow me-2 text-success"></i>
                Publications Before/After Fellowship
              </h5>
            </div>
            <div class="card-body">
              <div v-if="!hasPublicationData('publications_before_after')" class="text-center py-4 text-muted">
                <i class="bi bi-graph-up-arrow" style="font-size: 2rem;"></i>
                <p class="mt-2">No publication data available</p>
              </div>
              <div v-else>
                <canvas
                  ref="publicationsBeforeAfterChart"
                  style="max-height: 400px;"
                  width="800"
                  height="400"
                ></canvas>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Average Publications per Fellow -->
      <div class="row mb-4">
        <div class="col-12">
          <div class="card border-0 shadow-sm">
            <div class="card-header bg-white border-bottom">
              <h5 class="mb-0">
                <i class="bi bi-person-lines-fill me-2 text-info"></i>
                Average Publications per Fellow
              </h5>
            </div>
            <div class="card-body">
              <div v-if="!hasPublicationData('average_publications_per_fellow')" class="text-center py-4 text-muted">
                <i class="bi bi-person-lines-fill" style="font-size: 2rem;"></i>
                <p class="mt-2">No publication data available</p>
              </div>
              <div v-else>
                <canvas
                  ref="avgPublicationsPerFellowChart"
                  style="max-height: 400px;"
                  width="800"
                  height="400"
                ></canvas>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Additional Grants Awarded to Fellows -->
      <div class="row mb-4">
        <div class="col-12">
          <div class="card border-0 shadow-sm">
            <div class="card-header bg-white border-bottom">
              <h5 class="mb-0">
                <i class="bi bi-cash-stack me-2 text-success"></i>
                Additional Grants Awarded to Fellows
              </h5>
            </div>
            <div class="card-body">
              <div v-if="!hasGrantData('grants_per_year')" class="text-center py-4 text-muted">
                <i class="bi bi-cash-stack" style="font-size: 2rem;"></i>
                <p class="mt-2">No grant data available</p>
              </div>
              <div v-else>
                <canvas
                  ref="grantsChart"
                  style="max-height: 400px;"
                  width="800"
                  height="400"
                ></canvas>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Role of Fellow in Grant Awards -->
      <div class="row mb-4">
        <div class="col-12">
          <div class="card border-0 shadow-sm">
            <div class="card-header bg-white border-bottom">
              <h5 class="mb-0">
                <i class="bi bi-person-badge me-2 text-warning"></i>
                Role of Fellow in Grant Awards
              </h5>
            </div>
            <div class="card-body">
              <div v-if="!hasGrantData('grants_per_role')" class="text-center py-4 text-muted">
                <i class="bi bi-person-badge" style="font-size: 2rem;"></i>
                <p class="mt-2">No grant role data available</p>
              </div>
              <div v-else>
                <canvas
                  ref="grantRolesChart"
                  style="max-height: 400px;"
                  width="800"
                  height="400"
                ></canvas>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Total Grant Funding -->
      <div class="row mb-4">
        <div class="col-12">
          <div class="card border-0 shadow-sm">
            <div class="card-header bg-white border-bottom">
              <h5 class="mb-0">
                <i class="bi bi-currency-euro me-2 text-primary"></i>
                Total Grant Funding (Euros*)
              </h5>
            </div>
            <div class="card-body">
              <div v-if="!hasGrantData('funding_per_year')" class="text-center py-4 text-muted">
                <i class="bi bi-currency-euro" style="font-size: 2rem;"></i>
                <p class="mt-2">No funding data available</p>
              </div>
              <div v-else>
                <canvas
                  ref="fundingChart"
                  style="max-height: 400px;"
                  width="800"
                  height="400"
                ></canvas>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Funding Partners Table -->
      <div class="row mb-4">
        <div class="col-12">
          <div class="card border-0 shadow-sm">
            <div class="card-header bg-white border-bottom">
              <h5 class="mb-0">
                <i class="bi bi-building me-2 text-info"></i>
                Total Grant Funding Awarded by Partners to Fellows (Euros*)
              </h5>
            </div>
            <div class="card-body">
              <div v-if="fundingPartners.length === 0" class="text-center py-4 text-muted">
                <i class="bi bi-building" style="font-size: 2rem;"></i>
                <p class="mt-2">No funding partner data available</p>
              </div>
              <div v-else>
                <div class="d-flex justify-content-between align-items-center mb-3">
                  <div class="d-flex align-items-center">
                    <label class="me-2">Show</label>
                    <select v-model="partnersPageSize" class="form-select form-select-sm" style="width: auto;">
                      <option value="10">10</option>
                      <option value="25">25</option>
                      <option value="50">50</option>
                      <option value="100">100</option>
                    </select>
                    <span class="ms-2">entries</span>
                  </div>
                  <div class="d-flex align-items-center">
                    <label class="me-2">Search:</label>
                    <input
                      v-model="partnersSearch"
                      type="text"
                      class="form-control form-control-sm"
                      style="width: 200px;"
                      placeholder="Search partners..."
                    >
                  </div>
                </div>

                <div class="table-responsive">
                  <table class="table table-striped table-hover">
                    <thead class="table-light">
                    <tr>
                      <th>Partner</th>
                      <th class="text-end">Funding Awarded (Euros*)</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="partner in paginatedPartners" :key="partner.name">
                      <td>{{ partner.name }}</td>
                      <td class="text-end">{{ formatCurrency(partner.funding) }}</td>
                    </tr>
                    </tbody>
                  </table>
                </div>

                <!-- Pagination -->
                <div class="d-flex justify-content-between align-items-center mt-3" v-if="partnersTotalPages > 1">
                  <div class="text-muted">
                    Showing {{ partnersStartIndex + 1 }} to {{ partnersEndIndex }} of {{ filteredPartners.length }} entries
                  </div>
                  <nav>
                    <ul class="pagination pagination-sm mb-0">
                      <li class="page-item" :class="{ disabled: partnersCurrentPage === 1 }">
                        <button class="page-link" @click="partnersCurrentPage--" :disabled="partnersCurrentPage === 1">
                          Previous
                        </button>
                      </li>
                      <li
                        v-for="page in partnersPageNumbers"
                        :key="page"
                        class="page-item"
                        :class="{ active: page === partnersCurrentPage }"
                      >
                        <button class="page-link" @click="partnersCurrentPage = page">{{ page }}</button>
                      </li>
                      <li class="page-item" :class="{ disabled: partnersCurrentPage === partnersTotalPages }">
                        <button
                          class="page-link"
                          @click="partnersCurrentPage++"
                          :disabled="partnersCurrentPage === partnersTotalPages"
                        >
                          Next
                        </button>
                      </li>
                    </ul>
                  </nav>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Chart from 'chart.js/auto'
import ReportServices from '@/services/reports/ReportServices'

export default {
  name: 'FellowshipReports',

  data() {
    return {
      loading: false,
      exporting: false,
      error: null,
      chartsInitialized: false,
      mapInitialized: false,

      countryAliasMap: {
        // South Africa
        'ZA': 'South Africa',

        // East Africa
        'UG': 'Uganda',
        'KE': 'Kenya',
        'TZ': 'Tanzania',
        'TZAR': 'Tanzania',
        'RW': 'Rwanda',
        'BI': 'Burundi',
        'ET': 'Ethiopia',
        'SO': 'Somalia',
        'DJ': 'Djibouti',
        'ER': 'Eritrea',

        // Central Africa
        'CD': 'Democratic Republic of the Congo',
        'CG': 'Republic of the Congo',
        'CF': 'Central African Republic',
        'CENTRAL-AFRICAN-REPUBLIC': 'Central African Republic',
        'GA': 'Gabon',
        'GQ': 'Equatorial Guinea',
        'CM': 'Cameroon',

        // West Africa
        'NG': 'Nigeria',
        'GH': 'Ghana',
        'SL': 'Sierra Leone',
        'LR': 'Liberia',
        'CI': "Côte d'Ivoire",
        'IVORY COAST': "Côte d'Ivoire",
        'BF': 'Burkina Faso',
        'TG': 'Togo',
        'BJ': 'Benin',
        'ML': 'Mali',
        'SN': 'Senegal',
        'GN': 'Guinea',
        'GW': 'Guinea-Bissau',
        'GM': 'Gambia',
        'NE': 'Niger',
        'MR': 'Mauritania',

        // Southern Africa
        'ZW': 'Zimbabwe',
        'ZM': 'Zambia',
        'MW': 'Malawi',
        'BW': 'Botswana',
        'NA': 'Namibia',
        'MZ': 'Mozambique',
        'SZ': 'Eswatini',
        'SWAZILAND': 'Eswatini',
        'LS': 'Lesotho',
        'AO': 'Angola',
        'CV': 'Cape Verde',

        // Indian Ocean
        'MG': 'Madagascar',
        'MU': 'Mauritius',
        'KM': 'Comoros',
        'SC': 'Seychelles',

        // North Africa
        'SD': 'Sudan',
        'SS': 'South Sudan',
        'TN': 'Tunisia',
        'DZ': 'Algeria',
        'MA': 'Morocco',
        'LY': 'Libya',
        'EG': 'Egypt'
      },

      // Chart instances
      charts: {},

      // Pagination for partners table
      partnersSearch: '',
      partnersPageSize: 10,
      partnersCurrentPage: 1,

      // Data
      fellowshipStats: {
        fellowships_per_year: {},
        fellowships_per_call: [],
        gender_distribution: [],
        fellowships_per_country: { all: {} }
      },

      publicationStats: {
        publications_per_year: [],
        publications_during_fellowship: [],
        publications_before_after: [],
        average_publications_per_fellow: []
      },

      grantStats: {
        grants_per_year: [],
        grants_per_role: [],
        funding_per_year: [],
        funding_per_source: []
      },

      projectStats: {
        projects_per_year: [],
        students_supervised: { total: 0, by_year: {} }
      },

      fundingPartners: []
    }
  },

  computed: {
    sortedCountries() {
      const countries = this.fellowshipStats.fellowships_per_country?.all || {}
      return Object.fromEntries(
        Object.entries(countries).sort(([,a], [,b]) => b - a)
      )
    },

    filteredPartners() {
      if (!this.fundingPartners || this.fundingPartners.length === 0) {
        return []
      }
      return this.fundingPartners.filter(partner =>
        partner.name.toLowerCase().includes(this.partnersSearch.toLowerCase())
      )
    },

    partnersTotalPages() {
      return Math.ceil(this.filteredPartners.length / this.partnersPageSize)
    },

    partnersStartIndex() {
      return (this.partnersCurrentPage - 1) * this.partnersPageSize
    },

    partnersEndIndex() {
      return Math.min(this.partnersStartIndex + this.partnersPageSize, this.filteredPartners.length)
    },

    paginatedPartners() {
      if (!this.filteredPartners || this.filteredPartners.length === 0) {
        return []
      }
      return this.filteredPartners.slice(this.partnersStartIndex, this.partnersEndIndex)
    },

    partnersPageNumbers() {
      const pages = []
      const start = Math.max(1, this.partnersCurrentPage - 2)
      const end = Math.min(this.partnersTotalPages, this.partnersCurrentPage + 2)

      for (let i = start; i <= end; i++) {
        pages.push(i)
      }
      return pages
    }
  },

  async mounted() {
    // Load data from API first
    await this.loadAllData()

    // Watch for country data to initialize map
    this.$watch('fellowshipStats.fellowships_per_country', (newVal) => {
      if (newVal && Object.keys(newVal.all || {}).length > 0 && !this.mapInitialized && !this.loading) {
        this.$nextTick(() => {
          setTimeout(() => {
            this.initializeAfricaMap()
          }, 500)
        })
      }
    }, { deep: true, immediate: true })
  },

  beforeUnmount() {
    this.destroyAllCharts()
  },

  methods: {
    async loadAllData() {
      this.loading = true
      this.error = null

      try {

        const [fellowshipResponse, publicationResponse, grantResponse, projectResponse] = await Promise.all([
          ReportServices.getFellowshipStats().catch(err => ({ success: false, error: err })),
          ReportServices.getPublicationStats().catch(err => ({ success: false, error: err })),
          ReportServices.getGrantStats().catch(err => ({ success: false, error: err })),
          ReportServices.getProjectStats().catch(err => ({ success: false, error: err }))
        ])

        if (fellowshipResponse?.success && fellowshipResponse?.data) {
          // Convert API array format → expected { all: { country: count } }
          if (Array.isArray(fellowshipResponse.data.fellowships_per_country)) {
            const converted = {};
            fellowshipResponse.data.fellowships_per_country.forEach(item => {
              if (item.meta && item.value !== undefined) {
                converted[item.meta] = item.value;
              }
            });

            fellowshipResponse.data.fellowships_per_country = {
              all: converted
            };
          }

          // 🔹 NEW: filter out "Other" / "Others" / "Other 1" etc. from fellowship calls
          if (Array.isArray(fellowshipResponse.data.fellowships_per_call)) {
            fellowshipResponse.data.fellowships_per_call =
              fellowshipResponse.data.fellowships_per_call.filter(item => {
                const label = (item.meta || '').toString().toLowerCase().trim()
                // exclude items where meta is missing OR contains "other"
                return label && !label.includes('other')
              })
          }

          // Normalize country names in the fellowship data
          if (fellowshipResponse.data.fellowships_per_country?.all) {
            const normalizedCountries = {}
            Object.entries(fellowshipResponse.data.fellowships_per_country.all).forEach(([country, count]) => {
              const normalized = this.normalizeCountryName(country)
              if (normalized) {
                normalizedCountries[normalized] = (normalizedCountries[normalized] || 0) + count
              }
            })
            fellowshipResponse.data.fellowships_per_country.all = normalizedCountries
          }

          this.fellowshipStats = { ...this.fellowshipStats, ...fellowshipResponse.data }
        }

        if (publicationResponse?.success && publicationResponse?.data) {
          this.publicationStats = { ...this.publicationStats, ...publicationResponse.data }
        }

        if (grantResponse?.success && grantResponse?.data) {
          this.grantStats = { ...this.grantStats, ...grantResponse.data }

          if (grantResponse.data.funding_per_source) {
            this.fundingPartners = grantResponse.data.funding_per_source.map(item => ({
              name: item.meta || item.source || 'Unknown',
              funding: item.value || 0
            }))
          }
        }

        if (projectResponse?.success && projectResponse?.data) {
          this.projectStats = { ...this.projectStats, ...projectResponse.data }
        }

        await this.$nextTick()
        setTimeout(() => {
          this.initializeCharts()
        }, 500)

      } catch (error) {
        console.error('Error loading data:', error)
        this.error = 'Failed to load data from API. Please check your connection and try again.'
      } finally {
        this.loading = false
      }
    },

    normalizeCountryName(countryName) {
      if (!countryName || typeof countryName !== 'string') return null

      const formatted = countryName.trim().toUpperCase()

      // Handle abbreviation first
      if (this.countryAliasMap[formatted]) {
        return this.countryAliasMap[formatted]
      }

      // Convert common variations (existing logic)
      const titleCase = countryName
        .toLowerCase()
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')

      const countryMappings = {
        'Drc': 'Democratic Republic of the Congo',
        'Dr Congo': 'Democratic Republic of the Congo',
        'Congo Drc': 'Democratic Republic of the Congo',
        'Republic Of Congo': 'Republic of the Congo',
        'Ivory Coast': "Côte d'Ivoire",
        'Swaziland': 'Eswatini',
        'United Republic Of Tanzania': 'Tanzania'
      }

      return countryMappings[titleCase] || titleCase
    },

    // Data validation helpers
    hasData(key) {
      const data = this.fellowshipStats[key]
      if (Array.isArray(data)) {
        return data.length > 0
      }
      if (typeof data === 'object' && data !== null) {
        return Object.keys(data).length > 0
      }
      return false
    },

    hasPublicationData(key) {
      const data = this.publicationStats[key]
      return Array.isArray(data) && data.length > 0
    },

    hasGrantData(key) {
      const data = this.grantStats[key]
      return Array.isArray(data) && data.length > 0
    },

    hasProjectData() {
      const data = this.projectStats.projects_per_year
      return Array.isArray(data) && data.length > 0
    },

    hasStudentData() {
      const data = this.projectStats.students_supervised?.by_year
      return data && typeof data === 'object' && Object.keys(data).length > 0
    },

    // Map initialization
    async initializeAfricaMap() {
      if (this.mapInitialized || this.loading) return

      try {
        if (!window.L) await this.loadLeaflet()

        const mapContainer = document.getElementById('africa-map')
        if (!mapContainer) return

        mapContainer.innerHTML = ''

        const africaBounds = L.latLngBounds(
          L.latLng(-35, -25),
          L.latLng(37, 55)
        )

        const map = L.map('africa-map', {
          center: [0, 20],
          zoom: 3,
          minZoom: 3,
          maxZoom: 6,
          maxBounds: africaBounds,
          zoomControl: true,
          dragging: true,
          scrollWheelZoom: false,
          doubleClickZoom: false,
          attributionControl: false
        })

        const response = await fetch('https://raw.githubusercontent.com/datasets/geo-countries/master/data/countries.geojson')
        const worldData = await response.json()

        const africaCountries = [
          'Algeria', 'Angola', 'Benin', 'Botswana', 'Burkina Faso', 'Burundi',
          'Cameroon', 'Cape Verde', 'Central African Republic', 'Chad', 'Comoros',
          'Democratic Republic of the Congo', 'Republic of the Congo', 'Djibouti', 'Egypt',
          'Equatorial Guinea', 'Eritrea', 'Eswatini', 'Ethiopia', 'Gabon', 'Gambia', 'Ghana',
          'Guinea', 'Guinea-Bissau', 'Ivory Coast', 'Côte d\'Ivoire', 'Kenya', 'Lesotho',
          'Liberia', 'Libya', 'Madagascar', 'Malawi', 'Mali', 'Mauritania', 'Mauritius',
          'Morocco', 'Mozambique', 'Namibia', 'Niger', 'Nigeria', 'Rwanda',
          'São Tomé and Príncipe', 'Sao Tome and Principe', 'Senegal', 'Seychelles',
          'Sierra Leone', 'Somalia', 'South Africa', 'South Sudan', 'Sudan',
          'Tanzania', 'United Republic of Tanzania', 'Togo', 'Tunisia', 'Uganda',
          'Zambia', 'Zimbabwe'
        ]

        const africaData = {
          type: 'FeatureCollection',
          features: worldData.features.filter(feature => {
            const countryName = this.getCountryName(feature)
            return africaCountries.some(africanCountry =>
              countryName.toLowerCase() === africanCountry.toLowerCase()
            )
          })
        }

        const fellowshipData = this.fellowshipStats.fellowships_per_country?.all || {}

        const geojsonLayer = L.geoJSON(africaData, {
          style: (feature) => {
            const countryName = this.getCountryName(feature)
            let fellowCount = 0

            const matchingKey = Object.keys(fellowshipData).find(
              key => key.toLowerCase() === countryName.toLowerCase()
            )

            if (matchingKey) fellowCount = fellowshipData[matchingKey]

            let fillColor = '#CCEBEF'
            if (fellowCount >= 36) fillColor = '#009CAD'
            else if (fellowCount >= 21) fillColor = '#57BEC9'
            else if (fellowCount >= 10) fillColor = '#92D4DC'

            return {
              fillColor: fillColor,
              weight: 1.2,
              opacity: 0.8,
              color: '#ffffff',
              fillOpacity: 1
            }
          },
          onEachFeature: (feature, layer) => {
            const countryName = this.getCountryName(feature)
            let fellowCount = 0
            const matchingKey = Object.keys(fellowshipData).find(
              key => key.toLowerCase() === countryName.toLowerCase()
            )
            if (matchingKey) fellowCount = fellowshipData[matchingKey]

            layer.bindPopup(`
          <div class="map-popup">
            <strong>${countryName}</strong><br>
            Fellows: <span class="badge bg-primary">${fellowCount}</span>
          </div>
        `)

            layer.on({
              mouseover: (e) => {
                e.target.setStyle({ weight: 2.5, color: '#666', fillOpacity: 1 })
              },
              mouseout: (e) => {
                geojsonLayer.resetStyle(e.target)
              }
            })
          }
        }).addTo(map)

        this.addCountryMarkers(map, fellowshipData)
        map.fitBounds(africaBounds, { padding: [20, 20], maxZoom: 3.5 })
        this.mapInitialized = true

      } catch (error) {
        console.error('Error loading map:', error)
        const mapContainer = document.getElementById('africa-map')
        if (mapContainer) {
          mapContainer.innerHTML = `<div class="text-center text-muted py-5">
        <i class="bi bi-exclamation-triangle" style="font-size: 2rem;"></i>
        <p class="mt-2">Error loading map</p>
      </div>`
        }
      }
    },

    addCountryMarkers(map, fellowshipData) {
      Object.entries(fellowshipData).forEach(([country, count]) => {
        if (count > 0) {
          const coords = this.getCountryCoordinates(country)
          if (coords) {
            const icon = L.divIcon({
              className: 'my-div-icon',
              html: `<div style="background:#FFFFFF;border:2px solid #009CAD;border-radius:50% 50% 50% 0;width:30px;height:30px;display:flex;align-items:center;justify-content:center;box-shadow:0 3px 8px rgba(0,0,0,0.3);transform:rotate(-45deg);"><div style="transform:rotate(45deg);color:#009CAD;font-weight:bold;font-size:15px;">${count}</div></div>`,
              iconSize: [30, 30],
              iconAnchor: [10, 35]
            })
            L.marker(coords, { icon }).addTo(map)
          }
        }
      })
    },

    getCountryCoordinates(country) {
      const coords = {
        'South Africa': [-30.5595, 22.9375],
        'Kenya': [-0.0236, 37.9062],
        'Nigeria': [9.0820, 8.6753],
        'Ghana': [7.9465, -1.0232],
        'Tanzania': [-6.3690, 34.8888],
        'Egypt': [26.0975, 30.0444],
        'Morocco': [31.7917, -7.0926],
        'Uganda': [1.3733, 32.2903],
        'Ethiopia': [9.1450, 40.4897],
        'Zimbabwe': [-19.0154, 29.1549],
        'Zambia': [-13.1339, 27.8493],
        'Botswana': [-22.3285, 24.6849],
        'Namibia': [-22.9576, 18.4904],
        'Mali': [17.5707, -3.9962],
        'Burkina Faso': [12.2383, -1.5616],
        'Senegal': [14.4974, -14.4524],
        'Cameroon': [7.3697, 12.3547],
        'Madagascar': [-18.7669, 46.8691],
        'Angola': [-11.2027, 17.8739],
        'Mozambique': [-18.6657, 35.5296],

        'Democratic Republic of the Congo': [-4.0383, 21.7587],
        'Democratic Republic Of The Congo': [-4.0383, 21.7587],

        'Republic of the Congo': [-0.2280, 15.8277],
        'Republic Of The Congo': [-0.2280, 15.8277],

        'Algeria': [28.0339, 1.6596],
        'Tunisia': [33.8869, 9.5375],
        'Libya': [26.3351, 17.2283],
        'Sudan': [12.8628, 30.2176],
        'Chad': [15.4542, 18.7322],
        'Niger': [17.6078, 8.0817],
        'Mauritania': [21.0079, -10.9408],
        'Benin': [9.3077, 2.3158],
        'Togo': [8.6195, 0.8248],
        "Côte d'Ivoire": [7.5400, -5.5471],
        'Guinea': [9.9456, -9.6966],
        'Sierra Leone': [8.4606, -11.7799],
        'Liberia': [6.4281, -9.4295],
        'Gambia': [13.4432, -15.3101],
        'Guinea-Bissau': [11.8037, -15.1804],
        'Malawi': [-13.2543, 34.3015],
        'Rwanda': [-1.9403, 29.8739],
        'Burundi': [-3.3731, 29.9189],
        'Eritrea': [15.1794, 39.7823],
        'Djibouti': [11.8251, 42.5903],
        'Somalia': [5.1521, 46.1996],
        'Central African Republic': [6.6111, 20.9394],
        'Gabon': [-0.8037, 11.6094],
        'Equatorial Guinea': [1.6508, 10.2679],
        'Lesotho': [-29.6100, 28.2336],
        'Eswatini': [-26.5225, 31.4659]
      }
      return coords[country] || null
    },

    getCountryName(feature) {
      return feature.properties.NAME ||
        feature.properties.NAME_EN ||
        feature.properties.ADMIN ||
        feature.properties.name ||
        'Unknown'
    },

    async loadLeaflet() {
      return new Promise((resolve, reject) => {
        // Load CSS
        const cssLink = document.createElement('link')
        cssLink.rel = 'stylesheet'
        cssLink.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css'
        document.head.appendChild(cssLink)

        // Load JS
        const script = document.createElement('script')
        script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js'
        script.onload = () => {
          resolve()
        }
        script.onerror = () => {
          console.error('Failed to load Leaflet')
          reject(new Error('Failed to load Leaflet'))
        }
        document.head.appendChild(script)
      })
    },

    addMapLegend(map) {
      const legend = L.control({ position: 'bottomleft' })

      legend.onAdd = function() {
        const div = L.DomUtil.create('div', 'map-legend')
        div.style.backgroundColor = 'white'
        div.style.padding = '12px'
        div.style.border = '1px solid #ccc'
        div.style.borderRadius = '4px'
        div.style.fontSize = '12px'
        div.style.lineHeight = '18px'
        div.style.boxShadow = '0 2px 10px rgba(0,0,0,0.2)'

        div.innerHTML = `
          <div style="display: flex; align-items: center; margin: 2px 0;">
            <div style="width: 15px; height: 15px; background-color: #f0f0f0; margin-right: 8px; border: 1px solid #ccc;"></div>
            <span>Water Mass</span>
          </div>
          <div style="display: flex; align-items: center; margin: 2px 0;">
            <div style="width: 15px; height: 15px; background-color: #f0f0f0; margin-right: 8px; border: 1px solid #666;"></div>
            <span>0 Fellows in this country</span>
          </div>
          <div style="display: flex; align-items: center; margin: 2px 0;">
            <div style="width: 15px; height: 15px; background-color: #6baed6; margin-right: 8px; border: 1px solid #666;"></div>
            <span>1-4 Fellows in this country</span>
          </div>
          <div style="display: flex; align-items: center; margin: 2px 0;">
            <div style="width: 15px; height: 15px; background-color: #3182bd; margin-right: 8px; border: 1px solid #666;"></div>
            <span>5-9 Fellows in this country</span>
          </div>
          <div style="display: flex; align-items: center; margin: 2px 0;">
            <div style="width: 15px; height: 15px; background-color: #08519c; margin-right: 8px; border: 1px solid #666;"></div>
            <span>10+ Fellows in this country</span>
          </div>
        `

        return div
      }

      legend.addTo(map)
    },

    // Chart initialization methods
    initializeCharts() {
      if (this.chartsInitialized || this.loading) {
        return
      }

      this.destroyAllCharts()

      try {
        // Create charts individually and only if data exists and canvas is available
        this.initializeIndividualChart('fellowships_per_year', 'fellowshipsPerYearChart', this.createFellowshipsPerYearChart)
        this.initializeIndividualChart('fellowships_per_call', 'fellowshipCallsChart', this.createFellowshipCallsChart)
        this.initializeIndividualChart('gender_distribution', 'genderChart', this.createGenderChart)
        this.initializeIndividualChart('projects', 'projectsChart', this.createProjectsChart, 'projects')
        this.initializeIndividualChart('publications_per_year', 'publicationsChart', this.createPublicationsChart, 'publication')
        this.initializeIndividualChart('publications_during_fellowship', 'avgPublicationsDuringChart', this.createAvgPublicationsDuringChart, 'publication')
        this.initializeIndividualChart('publications_before_after', 'publicationsBeforeAfterChart', this.createPublicationsBeforeAfterChart, 'publication')
        this.initializeIndividualChart('average_publications_per_fellow', 'avgPublicationsPerFellowChart', this.createAvgPublicationsPerFellowChart, 'publication')
        this.initializeIndividualChart('grants_per_year', 'grantsChart', this.createGrantsChart, 'grant')
        this.initializeIndividualChart('grants_per_role', 'grantRolesChart', this.createGrantRolesChart, 'grant')
        this.initializeIndividualChart('funding_per_year', 'fundingChart', this.createFundingChart, 'grant')
        this.initializeIndividualChart('students', 'studentsChart', this.createStudentsChart, 'student')

        this.chartsInitialized = true
      } catch (error) {
        console.error('Error initializing charts:', error)
        this.chartsInitialized = false
      }
    },

    initializeIndividualChart(dataKey, canvasRef, createMethod, dataType = 'fellowship') {
      // Check if data exists
      let hasData = false
      switch (dataType) {
        case 'fellowship':
          hasData = this.hasData(dataKey)
          break
        case 'publication':
          hasData = this.hasPublicationData(dataKey)
          break
        case 'grant':
          hasData = this.hasGrantData(dataKey)
          break
        case 'student':
          hasData = this.hasStudentData()
          break
        case 'projects':
          hasData = this.hasProjectData()
          break
      }

      if (!hasData) {
        return
      }

      // Check if canvas exists
      const canvas = this.$refs[canvasRef]
      if (!canvas) {
        return
      }

      // Create the chart
      try {
        createMethod.call(this)
      } catch (error) {
        console.error(`Error creating chart ${canvasRef}:`, error)
      }
    },

    updateChartsWithNewData() {
      if (this.loading) {
        return
      }

      // Reset charts and reinitialize with new data
      this.chartsInitialized = false
      this.$nextTick(() => {
        setTimeout(() => {
          this.initializeCharts()
        }, 100)
      })
    },

    createFellowshipsPerYearChart() {
      try {
        const canvas = this.$refs.fellowshipsPerYearChart
        if (!canvas) return false

        const ctx = canvas.getContext('2d')
        if (!ctx) return false

        ctx.clearRect(0, 0, canvas.width, canvas.height)
        const data = this.prepareFellowshipsPerYearData()
        if (!data.labels || data.labels.length === 0) return false

        this.charts.fellowshipsPerYear = new Chart(ctx, {
          type: 'bar',
          data: data,
          options: {
            responsive: true,
            maintainAspectRatio: false,
            animation: { duration: 0 },
            plugins: {
              legend: { position: 'bottom' }
            },
            scales: {
              x: { stacked: true },
              y: {
                stacked: true,
                beginAtZero: true,
                ticks: { precision: 0 }
              }
            }
          }
        })
        return true
      } catch (error) {
        console.error('Error creating fellowships per year chart:', error)
        return false
      }
    },

    createFellowshipCallsChart() {
      try {
        const canvas = this.$refs.fellowshipCallsChart
        if (!canvas) return false

        const ctx = canvas.getContext('2d')
        if (!ctx) return false

        ctx.clearRect(0, 0, canvas.width, canvas.height)
        const data = this.fellowshipStats.fellowships_per_call || []
        if (data.length === 0) return false

        this.charts.fellowshipCalls = new Chart(ctx, {
          type: 'pie',
          data: {
            labels: data.map(item => item.meta || 'Unknown'),
            datasets: [{
              data: data.map(item => item.value || 0),
              backgroundColor: [
                'rgba(13, 110, 253, 0.8)',
                'rgba(25, 135, 84, 0.8)',
                'rgba(255, 193, 7, 0.8)',
                'rgba(220, 53, 69, 0.8)',
                'rgba(111, 66, 193, 0.8)',
                'rgba(23, 162, 184, 0.8)'
              ],
              borderWidth: 2,
              borderColor: '#fff'
            }]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            animation: { duration: 0 },
            plugins: {
              legend: { position: 'bottom' }
            }
          }
        })
        return true
      } catch (error) {
        console.error('Error creating fellowship calls chart:', error)
        return false
      }
    },

    createGenderChart() {
      try {
        const canvas = this.$refs.genderChart
        if (!canvas) return false

        const ctx = canvas.getContext('2d')
        if (!ctx) return false

        ctx.clearRect(0, 0, canvas.width, canvas.height)
        const data = this.fellowshipStats.gender_distribution || []
        if (data.length === 0) return false

        this.charts.gender = new Chart(ctx, {
          type: 'doughnut',
          data: {
            labels: data.map(item => item.meta || 'Unknown'),
            datasets: [{
              data: data.map(item => item.value || 0),
              backgroundColor: [
                'rgba(25, 135, 84, 0.8)',
                'rgba(13, 110, 253, 0.8)',
                'rgba(255, 193, 7, 0.8)',
                'rgba(108, 117, 125, 0.8)'
              ],
              borderWidth: 3,
              borderColor: '#fff'
            }]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            animation: { duration: 0 },
            plugins: {
              legend: { position: 'bottom' }
            }
          }
        })
        return true
      } catch (error) {
        console.error('Error creating gender chart:', error)
        return false
      }
    },

    createProjectsChart() {
      try {
        const canvas = this.$refs.projectsChart
        if (!canvas) return false

        const ctx = canvas.getContext('2d')
        if (!ctx) return false

        ctx.clearRect(0, 0, canvas.width, canvas.height)
        const data = this.projectStats.projects_per_year || []

        if (data.length === 0) return false

        this.charts.projects = new Chart(ctx, {
          type: 'bar',
          data: {
            labels: data.map(item => item.meta),
            datasets: [{
              label: 'Projects',
              data: data.map(item => item.value),
              backgroundColor: 'rgba(23, 162, 184, 0.8)',
              borderColor: 'rgba(23, 162, 184, 1)',
              borderWidth: 1
            }]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            animation: { duration: 0 },
            plugins: {
              legend: { display: false }
            },
            scales: {
              y: {
                beginAtZero: true,
                ticks: { precision: 0 }
              }
            }
          }
        })
        return true
      } catch (error) {
        console.error('Error creating projects chart:', error)
        return false
      }
    },

    createPublicationsChart() {
      try {
        const canvas = this.$refs.publicationsChart
        if (!canvas) return false

        const ctx = canvas.getContext('2d')
        if (!ctx) return false

        ctx.clearRect(0, 0, canvas.width, canvas.height)
        const data = this.publicationStats.publications_per_year || []

        if (data.length === 0) return false

        const duringData = data.map(item => item.during_fellowship || 0)
        const beforeAfterData = data.map(item => item.before_after_fellowship || item.value || 0)

        this.charts.publications = new Chart(ctx, {
          type: 'bar',
          data: {
            labels: data.map(item => item.meta || item.year),
            datasets: [
              {
                label: 'During EDCTP Fellowship',
                data: duringData,
                backgroundColor: 'rgba(23, 162, 184, 0.8)',
                borderColor: 'rgba(23, 162, 184, 1)',
                borderWidth: 1
              },
              {
                label: 'Before/After EDCTP Fellowship',
                data: beforeAfterData,
                backgroundColor: 'rgba(255, 193, 7, 0.8)',
                borderColor: 'rgba(255, 193, 7, 1)',
                borderWidth: 1
              }
            ]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            animation: { duration: 0 },
            plugins: {
              legend: { position: 'bottom' }
            },
            scales: {
              x: { stacked: true },
              y: {
                stacked: true,
                beginAtZero: true,
                ticks: { precision: 0 }
              }
            }
          }
        })
        return true
      } catch (error) {
        console.error('Error creating publications chart:', error)
        return false
      }
    },

    createAvgPublicationsDuringChart() {
      try {
        const canvas = this.$refs.avgPublicationsDuringChart
        if (!canvas) return false

        const ctx = canvas.getContext('2d')
        if (!ctx) return false

        ctx.clearRect(0, 0, canvas.width, canvas.height)
        const data = this.publicationStats.publications_during_fellowship || []

        if (data.length === 0) return false

        this.charts.avgPublicationsDuring = new Chart(ctx, {
          type: 'bar',
          data: {
            labels: data.map(item => item.meta || item.year),
            datasets: [{
              label: 'Publications during EDCTP Fellowship',
              data: data.map(item => item.value || 0),
              backgroundColor: 'rgba(23, 162, 184, 0.8)',
              borderColor: 'rgba(23, 162, 184, 1)',
              borderWidth: 1
            }]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            animation: { duration: 0 },
            plugins: {
              legend: { display: false }
            },
            scales: {
              y: {
                beginAtZero: true,
                ticks: { precision: 0 }
              }
            }
          }
        })
        return true
      } catch (error) {
        console.error('Error creating avg publications during chart:', error)
        return false
      }
    },

    createPublicationsBeforeAfterChart() {
      try {
        const canvas = this.$refs.publicationsBeforeAfterChart
        if (!canvas) return false

        const ctx = canvas.getContext('2d')
        if (!ctx) return false

        ctx.clearRect(0, 0, canvas.width, canvas.height)
        const data = this.publicationStats.publications_before_after || []

        if (data.length === 0) return false

        this.charts.publicationsBeforeAfter = new Chart(ctx, {
          type: 'bar',
          data: {
            labels: data.map(item => item.meta || item.year),
            datasets: [{
              label: 'Publications before/after EDCTP Fellowship',
              data: data.map(item => item.value || 0),
              backgroundColor: 'rgba(23, 162, 184, 0.8)',
              borderColor: 'rgba(23, 162, 184, 1)',
              borderWidth: 1
            }]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            animation: { duration: 0 },
            plugins: {
              legend: { display: false }
            },
            scales: {
              y: {
                beginAtZero: true,
                ticks: { precision: 0 }
              }
            }
          }
        })
        return true
      } catch (error) {
        console.error('Error creating publications before/after chart:', error)
        return false
      }
    },

    createAvgPublicationsPerFellowChart() {
      try {
        const canvas = this.$refs.avgPublicationsPerFellowChart
        if (!canvas) return false

        const ctx = canvas.getContext('2d')
        if (!ctx) return false

        ctx.clearRect(0, 0, canvas.width, canvas.height)
        const data = this.publicationStats.average_publications_per_fellow || []

        if (data.length === 0) return false

        this.charts.avgPublicationsPerFellow = new Chart(ctx, {
          type: 'bar',
          data: {
            labels: data.map(item => item.meta || item.year),
            datasets: [{
              label: 'During EDCTP Fellowship + before/after EDCTP Fellowship',
              data: data.map(item => item.value || 0),
              backgroundColor: 'rgba(23, 162, 184, 0.8)',
              borderColor: 'rgba(23, 162, 184, 1)',
              borderWidth: 1
            }]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            animation: { duration: 0 },
            plugins: {
              legend: { display: false }
            },
            scales: {
              y: {
                beginAtZero: true,
                ticks: { precision: 0 }
              }
            }
          }
        })
        return true
      } catch (error) {
        console.error('Error creating avg publications per fellow chart:', error)
        return false
      }
    },

    createGrantsChart() {
      try {
        const canvas = this.$refs.grantsChart
        if (!canvas) return false

        const ctx = canvas.getContext('2d')
        if (!ctx) return false

        ctx.clearRect(0, 0, canvas.width, canvas.height)
        const data = this.grantStats.grants_per_year || []

        if (data.length === 0) return false

        this.charts.grants = new Chart(ctx, {
          type: 'bar',
          data: {
            labels: data.map(item => item.meta || item.year),
            datasets: [{
              label: 'Grants',
              data: data.map(item => item.value || 0),
              backgroundColor: 'rgba(25, 135, 84, 0.8)',
              borderColor: 'rgba(25, 135, 84, 1)',
              borderWidth: 1
            }]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            animation: { duration: 0 },
            plugins: {
              legend: { display: false }
            },
            scales: {
              y: {
                beginAtZero: true,
                ticks: { precision: 0 }
              }
            }
          }
        })
        return true
      } catch (error) {
        console.error('Error creating grants chart:', error)
        return false
      }
    },

    createGrantRolesChart() {
      try {
        const canvas = this.$refs.grantRolesChart
        if (!canvas) return false

        const ctx = canvas.getContext('2d')
        if (!ctx) return false

        ctx.clearRect(0, 0, canvas.width, canvas.height)
        const data = this.grantStats.grants_per_role || []

        if (data.length === 0) return false

        this.charts.grantRoles = new Chart(ctx, {
          type: 'pie',
          data: {
            labels: data.map(item => item.meta || 'Unknown'),
            datasets: [{
              data: data.map(item => item.value || 0),
              backgroundColor: [
                'rgba(23, 162, 184, 0.8)',
                'rgba(25, 135, 84, 0.8)',
                'rgba(255, 193, 7, 0.8)',
                'rgba(220, 53, 69, 0.8)',
                'rgba(111, 66, 193, 0.8)'
              ],
              borderWidth: 2,
              borderColor: '#fff'
            }]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            animation: { duration: 0 },
            plugins: {
              legend: { position: 'bottom' }
            }
          }
        })
        return true
      } catch (error) {
        console.error('Error creating grant roles chart:', error)
        return false
      }
    },

    createFundingChart() {
      try {
        const canvas = this.$refs.fundingChart
        if (!canvas) return false

        const ctx = canvas.getContext('2d')
        if (!ctx) return false

        ctx.clearRect(0, 0, canvas.width, canvas.height)
        const data = this.grantStats.funding_per_year || []

        if (data.length === 0) return false

        this.charts.funding = new Chart(ctx, {
          type: 'bar',
          data: {
            labels: data.map(item => item.meta || item.year),
            datasets: [{
              label: 'Total per year',
              data: data.map(item => item.value || 0),
              backgroundColor: 'rgba(13, 110, 253, 0.8)',
              borderColor: 'rgba(13, 110, 253, 1)',
              borderWidth: 1
            }]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            animation: { duration: 0 },
            plugins: {
              legend: { display: false },
              tooltip: {
                callbacks: {
                  label: (context) => {
                    return `€${this.formatCurrency(context.parsed.y)}`
                  }
                }
              }
            },
            scales: {
              y: {
                beginAtZero: true,
                ticks: {
                  callback: function(value) {
                    if (value >= 1000000) {
                      return '€' + (value / 1000000).toFixed(1) + 'M'
                    } else if (value >= 1000) {
                      return '€' + (value / 1000).toFixed(0) + 'K'
                    }
                    return '€' + value
                  }
                }
              }
            }
          }
        })
        return true
      } catch (error) {
        console.error('Error creating funding chart:', error)
        return false
      }
    },

    createStudentsChart() {
      try {
        const canvas = this.$refs.studentsChart
        if (!canvas) return false

        const ctx = canvas.getContext('2d')
        if (!ctx) return false

        ctx.clearRect(0, 0, canvas.width, canvas.height)
        const data = this.projectStats.students_supervised?.by_year || {}

        if (Object.keys(data).length === 0) return false

        const years = Object.keys(data).sort()
        const duringData = years.map(year => data[year]?.during || 0)
        const afterData = years.map(year => data[year]?.after || data[year]?.before_after || 0)

        this.charts.students = new Chart(ctx, {
          type: 'bar',
          data: {
            labels: years,
            datasets: [
              {
                label: 'Students supervised (during EDCTP Fellowship)',
                data: duringData,
                backgroundColor: 'rgba(23, 162, 184, 0.8)',
                borderColor: 'rgba(23, 162, 184, 1)',
                borderWidth: 1
              },
              {
                label: 'Students supervised (before/after EDCTP Fellowship)',
                data: afterData,
                backgroundColor: 'rgba(255, 193, 7, 0.8)',
                borderColor: 'rgba(255, 193, 7, 1)',
                borderWidth: 1
              }
            ]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            animation: { duration: 0 },
            plugins: {
              legend: { position: 'bottom' }
            },
            scales: {
              x: { stacked: true },
              y: {
                stacked: true,
                beginAtZero: true,
                ticks: { precision: 0 }
              }
            }
          }
        })
        return true
      } catch (error) {
        console.error('Error creating students chart:', error)
        return false
      }
    },

    prepareFellowshipsPerYearData() {
      const yearData = this.fellowshipStats.fellowships_per_year || {}
      const years = Object.keys(yearData).sort()

      if (years.length === 0) {
        return { labels: [], datasets: [] }
      }

      // Get all unique call types
      const callTypes = new Set()
      years.forEach(year => {
        const yearArray = yearData[year]
        if (Array.isArray(yearArray)) {
          yearArray.forEach(item => {
            if (item && item.meta) {
              callTypes.add(item.meta)
            }
          })
        }
      })

      const callTypesArray = Array.from(callTypes)
      const colors = [
        'rgba(23, 162, 184, 0.8)',     // CDF - Teal
        'rgba(255, 193, 7, 0.8)',      // SF - Yellow
        'rgba(220, 53, 69, 0.8)',      // R&D F - Red
        'rgba(111, 66, 193, 0.8)',     // PF - Purple
        'rgba(192, 192, 192, 0.8)',    // EPI - Gray
        'rgba(40, 167, 69, 0.8)'       // Female PhD - Green
      ]

      const datasets = callTypesArray.map((callType, index) => ({
        label: callType,
        data: years.map(year => {
          const yearArray = yearData[year]
          if (Array.isArray(yearArray)) {
            const item = yearArray.find(i => i.meta === callType)
            return item ? item.value : 0
          }
          return 0
        }),
        backgroundColor: colors[index % colors.length],
        borderColor: colors[index % colors.length].replace('0.8', '1'),
        borderWidth: 1
      }))

      return {
        labels: years,
        datasets: datasets
      }
    },

    updateAllCharts() {
      Object.keys(this.charts).forEach(chartKey => {
        if (this.charts[chartKey]) {
          try {
            this.charts[chartKey].update('none')
          } catch (error) {
            console.warn(`Error updating chart ${chartKey}:`, error)
          }
        }
      })
    },

    destroyAllCharts() {
      Object.keys(this.charts).forEach(key => {
        if (this.charts[key] && typeof this.charts[key].destroy === 'function') {
          try {
            this.charts[key].destroy()
          } catch (error) {
            console.warn(`Error destroying chart ${key}:`, error)
          }
          delete this.charts[key]
        }
      })
      this.chartsInitialized = false
    },

    getTotalFellowshipCalls() {
      const calls = this.fellowshipStats.fellowships_per_call || []
      return calls.reduce((total, call) => total + (call.value || 0), 0)
    },

    formatCurrency(amount) {
      return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(amount || 0)
    },

    async refreshAllData() {
      await this.loadAllData()
    },

    async exportToExcel() {
      this.exporting = true
      try {
        await ReportServices.exportToExcel()
      } catch (error) {
        console.error('Export error:', error)
        this.error = 'Failed to export data. Please try again.'
      } finally {
        this.exporting = false
      }
    }
  }
}
</script>

<style scoped>
#africa-map {
  border-radius: 12px;
  background: #FFFFFF !important;
}

.leaflet-container {
  background: #FFFFFF !important;
}

.leaflet-popup-content-wrapper {
  border-radius: 10px;
  box-shadow: 0 4px 15px rgba(13, 115, 119, 0.2);
  border: 2px solid #80ede9;
}

.map-popup .badge {
  background: #0d7377 !important;
}

.leaflet-control-attribution {
  display: none !important;
}

.leaflet-control-zoom {
  border: 2px solid #0d7377 !important;
  border-radius: 8px !important;
}

.leaflet-control-zoom a {
  background: white !important;
  color: #0d7377 !important;
}

.leaflet-control-zoom a:hover {
  background: #0d7377 !important;
  color: white !important;
}

.fellowship-reports {
  padding: 1.5rem 0;
}

.card {
  border: none;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.card:hover {
  transform: translateY(-2px);
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15) !important;
}

.card-body {
  padding: 1.5rem;
}

canvas {
  max-width: 100%;
  height: auto !important;
}

.country-stats {
  height: 100%;
}

.country-list {
  font-size: 0.9rem;
}

.country-name {
  font-weight: 500;
}

.table th {
  font-weight: 600;
  background-color: #f8f9fa;
}

.badge {
  font-size: 0.75rem;
}

#africa-map {
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  border: 2px dashed #dee2e6;
}

.pagination-sm .page-link {
  padding: 0.25rem 0.5rem;
  font-size: 0.875rem;
}

.alert-info {
  background-color: #e7f3ff;
  border-color: #b8daff;
  color: #004085;
}

/* Map Legend Styles */
.map-legend {
  background: white !important;
  padding: 10px !important;
  border: 2px solid #ccc !important;
  border-radius: 5px !important;
  font-size: 12px !important;
  box-shadow: 0 2px 10px rgba(0,0,0,0.2) !important;
}

/* Map styling improvements */
#africa-map {
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  border: 2px dashed #dee2e6;
  cursor: default !important; /* Remove pointer cursor */
}

#africa-map .leaflet-container {
  border-radius: 8px;
  cursor: default !important; /* Remove pointer cursor */
}

/* Disable leaflet zoom controls */
#africa-map .leaflet-control-zoom {
  display: none !important;
}

/* Style the map container */
#africa-map .leaflet-container a {
  cursor: default !important;
}

#africa-map .leaflet-clickable {
  cursor: default !important;
}

/* Loading state improvements */
.spinner-border {
  animation: spinner-border 0.75s linear infinite;
}

@keyframes spinner-border {
  to {
    transform: rotate(360deg);
  }
}

/* Empty state styling */
.text-muted i {
  opacity: 0.5;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .fellowship-reports {
    padding: 1rem 0;
  }

  .card-body {
    padding: 1rem;
  }

  #africa-map {
    height: 300px !important;
  }

  .country-list {
    max-height: 250px !important;
  }
}

/* Chart container improvements */
canvas {
  background: transparent;
}

/* Table styling improvements */
.table-responsive {
  border-radius: 0.375rem;
  border: 1px solid #dee2e6;
}

.table thead th {
  border-bottom: 2px solid #dee2e6;
  position: sticky;
  top: 0;
  background-color: #f8f9fa;
  z-index: 10;
}

/* Button improvements */
.btn {
  border-radius: 0.375rem;
  font-weight: 500;
}

.btn:disabled {
  opacity: 0.6;
}

/* Alert improvements */
.alert {
  border-radius: 0.5rem;
  border: none;
}

.alert-danger {
  background-color: #f8d7da;
  color: #721c24;
}

.alert-info {
  background-color: #d1ecf1;
  color: #0c5460;
}

/* Card header improvements */
.card-header {
  background-color: #fff !important;
  border-bottom: 1px solid #dee2e6;
  padding: 1.25rem 1.5rem;
}

.card-title {
  color: #495057;
  font-weight: 600;
}

/* Badge improvements */
.badge {
  font-weight: 500;
  padding: 0.375rem 0.75rem;
}

.badge.bg-primary {
  background-color: #0d6efd !important;
}

.badge.bg-light {
  background-color: #f8f9fa !important;
  color: #495057 !important;
  border: 1px solid #dee2e6;
}

/* Form control improvements */
.form-control:focus,
.form-select:focus {
  border-color: #86b7fe;
  box-shadow: 0 0 0 0.25rem rgba(13, 110, 253, 0.25);
}

/* Pagination improvements */
.pagination {
  margin-bottom: 0;
}

.page-link {
  color: #0d6efd;
  border-color: #dee2e6;
}

.page-link:hover {
  color: #0a58ca;
  background-color: #e9ecef;
}

.page-item.active .page-link {
  background-color: #0d6efd;
  border-color: #0d6efd;
}
</style>
